import React, {ComponentRef, PropsWithChildren, useCallback, useRef} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {createPortal} from 'react-dom';
import {useClickOutsideOf} from 'hooks';

interface IModalProps extends PropsWithChildren {
  isVisible: boolean;
  handlePressOutside?: () => void;
  usePortal?: boolean;
}

const Modal = ({isVisible, handlePressOutside, usePortal = false, children}: IModalProps) => {
  if (usePortal) {
    return createPortal(<ModalContent isVisible={isVisible} handlePressOutside={handlePressOutside} children={children} />, document.body);
  }
  return <ModalContent isVisible={isVisible} handlePressOutside={handlePressOutside} children={children} />;
};

export default React.memo(Modal);

interface IModalContentProps extends PropsWithChildren {
  isVisible: boolean;
  handlePressOutside?: () => void;
}

const ModalContent = React.memo(({isVisible, handlePressOutside, children}: IModalContentProps) => {
  const ref = useRef<ComponentRef<'div'>>(null);

  useClickOutsideOf(
    ref,
    useCallback(() => {
      handlePressOutside?.();
    }, [handlePressOutside]),
    isVisible,
  );

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed inset-0 z-10 flex items-center justify-center px-2 pb-14">
          <div className="absolute inset-0 bg-black opacity-50"></div>
          <motion.div
            ref={ref}
            initial={{opacity: 0, translateY: 10}}
            animate={{opacity: 1, translateY: 0}}
            exit={{opacity: 0, translateY: -10}}
            className="relative max-w-md w-full">
            <div className="w-full bg-white p-4 rounded-md">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
});
